<template>
  <div class="pwt-trip-nav-bar">
    <BackBtn v-if="showBreadcrumbs" />
    <CustomBreadcrumbs v-if="showBreadcrumbs" :items="items" />
  </div>
</template>

<script setup lang="ts">
import { useTripStore } from '@/domains/trips'
import { type RouteName } from '@/router'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import BackBtn from '../buttons/BackBtn.vue'
import CustomBreadcrumbs, { type BreadcrumbsItem } from './CustomBreadcrumbs.vue'

const route = useRoute()
const tripStore = useTripStore()
const trip = computed(() => tripStore.getSelectedTrip)
const { smAndDown } = useDisplay()

const showBreadcrumbs = computed(() => {
  if (route.meta && route.meta.section === 'trip') return true
  return false
})

type Breadcrumb = {
  title: string,
  to: { name: RouteName}
  preserveQuery: boolean
}

// --- possible breadcrumbs --- //
const destShort: Breadcrumb = {
  title: 'Destination',
  to: { name: 'trip-add-destination' },
  preserveQuery: true,
}
const destLong: Breadcrumb  = {
  title: 'Add destination',
  to: { name: 'trip-add-destination' },
  preserveQuery: true,
}
const origShort: Breadcrumb  = {
  title: 'Origin',
  to: { name: 'trip-add-origin' },
  preserveQuery: true,
}
const origLong: Breadcrumb  = {
  title: 'Add origin',
  to: { name: 'trip-add-origin' },
  preserveQuery: true,
}
const detailsShort: Breadcrumb  = {
  title: 'Details',
  to: { name: 'trip-add-details' },
  preserveQuery: true,
}
const detailsLong: Breadcrumb  = {
  title: 'Add details',
  to: { name: 'trip-add-details' },
  preserveQuery: true,
}
const itinerary: Breadcrumb  = {
  title: 'Itinerary',
  to: { name: 'trip-itinerary' },
  preserveQuery: true,
}
const stats: Breadcrumb  = {
  title: 'Stats',
  to: { name: 'trip-stats' },
  preserveQuery: true,
}

// compile breadcrumbs
const items = computed<BreadcrumbsItem[]>(() => {
  const plannedTrip = !!(trip.value && trip.value.status && trip.value.status !== 'unplanned')
  const useShort = smAndDown.value

  const allBreadcrumbsShort = [
    destShort,
    origShort,
    detailsShort,
    itinerary,
  ]
  const allBreadcrumbsLong = [destLong, origLong, detailsLong, itinerary]

  if (route.name as RouteName === 'trip-add-destination' ) {
    if (plannedTrip) return useShort ? allBreadcrumbsShort : allBreadcrumbsLong
    return []
  }

  if (route.name as RouteName  === 'trip-add-origin') {
    if (plannedTrip) return useShort ? allBreadcrumbsShort : allBreadcrumbsLong
    return useShort ? [destShort, origShort] : [destLong, origLong]
  }

  if (route.name as RouteName  === 'trip-add-details') {
    if (plannedTrip) return useShort ? allBreadcrumbsShort : allBreadcrumbsLong
    return useShort ? [destShort, origShort, detailsShort] : [destLong, origLong, detailsLong]
  }

  if (route.name as RouteName  === 'trip-itinerary') {
    return useShort ? allBreadcrumbsShort : allBreadcrumbsLong
  }

  if (route.name as RouteName  === 'trip-stats') {
    return useShort ? [...allBreadcrumbsShort, stats] : [...allBreadcrumbsLong, stats]
  }

  return []
})
</script>

<style>
.pwt-trip-nav-bar {
  flex-direction: row;
  display: flex;
  flex-wrap: nowrap;
  margin: 0 20px;
  width: calc(100% - 40px);
}
</style>
